import React, { useEffect, useRef, useState } from "react";
import { Map as LeafletMap } from "leaflet";
import L from "leaflet";
import { RoutePlayer } from "../routes/RoutePlayer";
import { RouteTimeline } from "../routes/RouteTimeline";
import { RouteAnalytics } from "../routes/RouteAnalytics";
import { RoutePaths } from "../routes/RoutePaths";
import { RouteHeatmapLayer } from "../map/RouteHeatmapLayer";
import { HeatmapControls } from "./HeatmapControls";
import { DateRangePicker } from "./DateRangePicker";
import { BikeSelector } from "./BikeSelector";
import { ExportControls } from "./ExportControls";
import { MapContainer, TileLayer } from "react-leaflet";
import { useRouteStore } from "../../store/useRouteStore";
import { useBikeStore } from "../../store/useBikeStore";
import { MAP_CONFIG } from "../../utils/constants";
import { LoadingSpinner } from "../common/LoadingSpinner";
import { cn } from "../../utils/helpers";
import { Maximize2, Minimize2, Expand, Eye, EyeOff } from "lucide-react";
import type { HeatmapOptions, HeatmapData } from "../../types/route";
import { formatDuration } from "../../utils/formatters";

export const HistoryView: React.FC = () => {
  const [selectedBikes, setSelectedBikes] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0],
    end: new Date().toISOString().split("T")[0],
  });

  // Map controls state
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [showRoutes, setShowRoutes] = useState(true);
  const [heatmapData, setHeatmapData] = useState<HeatmapData | undefined>();
  const [heatmapOptions, setHeatmapOptions] = useState<Partial<HeatmapOptions>>(
    {
      radius: 20,
      blur: 15,
      minOpacity: 0.3,
      maxOpacity: 0.8,
    },
  );

  const mapRef = useRef<LeafletMap | null>(null);

  const {
    routes,
    selectedRoute,
    loadingRoutes,
    fetchMultipleRoutes,
    selectRoute,
    showStops,
    showSegments,
    showGpsPoints,
    colorBySpeed,
    setShowStops,
    setShowSegments,
    setShowGpsPoints,
    setColorBySpeed,
  } = useRouteStore();

  const { bikes, loadIfNeeded } = useBikeStore();

  const handleLoadRoutes = async () => {
    if (selectedBikes.length === 0) return;

    const startTime = new Date(dateRange.start + "T00:00:00").toISOString();
    const endTime = new Date(dateRange.end + "T23:59:59").toISOString();

    await fetchMultipleRoutes(selectedBikes, startTime, endTime);
  };

  const handleRouteSelect = (bikeId: string) => {
    if (!bikeId) {
      selectRoute(null);
      return;
    }
    const route = routes.find((r) => r.bike_id === bikeId);
    selectRoute(route || null);
  };

  const totalTrips = routes.reduce((sum, route) => sum + route.total_trips, 0);
  const totalActiveMinutes = routes.reduce(
    (sum, route) => sum + route.duration_minutes,
    0,
  );

  // Fit map to show all route points
  const handleFitToRoutes = () => {
    if (!mapRef.current || routes.length === 0) return;

    const allPoints = routes.flatMap((route) =>
      route.coordinates.map(
        (coord) => [coord.latitude, coord.longitude] as [number, number],
      ),
    );

    if (allPoints.length === 0) return;

    const group = new L.FeatureGroup(allPoints.map((point) => L.marker(point)));

    mapRef.current.fitBounds(group.getBounds(), {
      padding: [10, 10],
      maxZoom: 16,
    });
  };

  // Toggle fullscreen mode
  const handleToggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const mapControlButtons = [
    {
      icon: showRoutes ? Eye : EyeOff,
      label: showRoutes ? "Hide Routes" : "Show Routes",
      onClick: () => setShowRoutes(!showRoutes),
      active: showRoutes,
    },
    {
      icon: Expand,
      label: "Fit to Routes",
      onClick: handleFitToRoutes,
      active: false,
      disabled: routes.length === 0,
    },
    {
      icon: isFullscreen ? Minimize2 : Maximize2,
      label: isFullscreen ? "Exit Fullscreen" : "Fullscreen Map",
      onClick: handleToggleFullscreen,
      active: isFullscreen,
    },
  ];

  useEffect(() => {
    loadIfNeeded().catch(console.error);
  }, [loadIfNeeded]);

  // Leaflet must recalculate size after sidebar/header layout changes
  useEffect(() => {
    if (!mapRef.current) return;

    const id = window.setTimeout(() => {
      mapRef.current?.invalidateSize();
    }, 0);

    return () => window.clearTimeout(id);
  }, [isFullscreen]);

  return (
    <div className="h-full flex flex-col">
      {/* Controls Header - Hide in fullscreen */}
      {!isFullscreen && (
        <div className="bg-white shadow-sm border-b border-gray-200 p-4">
          <div className="flex flex-wrap items-center gap-4">
            <DateRangePicker
              startDate={dateRange.start}
              endDate={dateRange.end}
              onChange={setDateRange}
            />

            <BikeSelector
              bikes={bikes}
              selectedBikes={selectedBikes}
              onChange={setSelectedBikes}
            />

            <button
              onClick={handleLoadRoutes}
              disabled={selectedBikes.length === 0 || loadingRoutes}
              className={cn(
                "px-4 py-2 rounded-md font-medium transition-all duration-200 flex items-center space-x-2 min-w-[120px]",
                selectedBikes.length === 0 || loadingRoutes
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-blue-600 text-white hover:bg-blue-700 hover:shadow-md",
              )}
            >
              {loadingRoutes ? (
                <>
                  <LoadingSpinner size="sm" color="white" />
                  <span>Loading...</span>
                </>
              ) : (
                <span>Load Routes</span>
              )}
            </button>

            {/* Export Controls */}
            {routes.length > 0 && (
              <ExportControls routes={routes} selectedRoute={selectedRoute} />
            )}

            {/* Display Options */}
            {routes.length > 0 && (
              <div className="flex items-center space-x-4 border-l border-gray-300 pl-4">
                <label className="flex items-center text-sm">
                  <input
                    type="checkbox"
                    checked={showStops}
                    onChange={(e) => setShowStops(e.target.checked)}
                    className="mr-2"
                  />
                  Show Stops
                </label>
                <label className="flex items-center text-sm">
                  <input
                    type="checkbox"
                    checked={colorBySpeed}
                    onChange={(e) => setColorBySpeed(e.target.checked)}
                    className="mr-2"
                  />
                  Color by Speed
                </label>
                <label className="flex items-center text-sm">
                  <input
                    type="checkbox"
                    checked={showSegments}
                    onChange={(e) => setShowSegments(e.target.checked)}
                    className="mr-2"
                  />
                  Show Gap Links
                </label>
                <label
                  className={cn(
                    "flex items-center text-sm",
                    !selectedRoute && "opacity-50",
                  )}
                >
                  <input
                    type="checkbox"
                    checked={showGpsPoints}
                    onChange={(e) => setShowGpsPoints(e.target.checked)}
                    disabled={!selectedRoute}
                    className="mr-2"
                  />
                  Show GPS Points
                </label>
              </div>
            )}

            {/* Route Selection */}
            {routes.length > 0 && (
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">
                  Focus Route:
                </label>
                <select
                  value={selectedRoute?.bike_id || ""}
                  onChange={(e) => handleRouteSelect(e.target.value)}
                  className="text-sm border border-gray-300 rounded px-2 py-1"
                >
                  <option value="">All Routes</option>
                  {routes.map((route) => (
                    <option key={route.bike_id} value={route.bike_id}>
                      {route.bike_id} ({route.total_trips} segments,{" "}
                      {route.total_distance_km.toFixed(1)} km)
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex">
        {/* Left Panel - Controls and Analytics - Hide in fullscreen */}
        {!isFullscreen && (
          <div className="h-0 min-h-full w-96 bg-gray-50 border-r border-gray-200 overflow-y-auto">
            <div className="p-4 space-y-4">
              {/* Heatmap Controls */}
              {routes.length > 0 && (
                <HeatmapControls
                  enabled={showHeatmap}
                  onToggle={setShowHeatmap}
                  options={heatmapOptions}
                  onOptionsChange={setHeatmapOptions}
                  heatmapData={heatmapData}
                />
              )}

              {selectedRoute && <RoutePlayer />}
              {selectedRoute && <RouteTimeline />}
              {selectedRoute && <RouteAnalytics route={selectedRoute} />}
            </div>
          </div>
        )}

        {/* Right Panel - Map */}
        <div className="flex-1 relative">
          {loadingRoutes && (
            <div className="absolute inset-0 bg-white/75 flex items-center justify-center z-10">
              <div className="text-center">
                <LoadingSpinner size="lg" />
                <p className="mt-2 text-gray-600">Loading routes...</p>
              </div>
            </div>
          )}

          <MapContainer
            center={MAP_CONFIG.DEFAULT_CENTER}
            zoom={MAP_CONFIG.DEFAULT_ZOOM}
            zoomDelta={MAP_CONFIG.ZOOM_DELTA}
            zoomSnap={MAP_CONFIG.ZOOM_SNAP}
            maxZoom={MAP_CONFIG.MAX_ZOOM}
            wheelPxPerZoomLevel={MAP_CONFIG.WHEEL_PX_PER_ZOOM_LEVEL}
            style={{ height: "100%", width: "100%" }}
            ref={mapRef}
            className="z-0"
          >
            <TileLayer
              url={MAP_CONFIG.TILE_URL}
              attribution={MAP_CONFIG.TILE_ATTRIBUTION}
            />

            {/* Route Paths */}
            {routes.length > 0 && showRoutes && <RoutePaths routes={routes} />}

            {/* Heatmap Layer */}
            {routes.length > 0 && (
              <RouteHeatmapLayer
                routes={routes}
                enabled={showHeatmap}
                options={heatmapOptions}
                onDataUpdate={setHeatmapData}
              />
            )}
          </MapContainer>

          {/* Map Controls Overlay */}
          <div className="absolute top-4 right-4 z-[9999] space-y-2">
            {mapControlButtons.map((button, index) => (
              <button
                key={index}
                onClick={button.onClick}
                disabled={button.disabled}
                className={cn(
                  "p-2 rounded-lg shadow-md transition-all duration-200 flex items-center justify-center",
                  button.disabled
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : button.active
                      ? "bg-blue-600 text-white hover:bg-blue-700"
                      : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-200",
                )}
                title={button.label}
              >
                <button.icon className="w-4 h-4" />
              </button>
            ))}
          </div>

          {/* Heatmap Controls Overlay - Show in fullscreen */}
          {isFullscreen && routes.length > 0 && (
            <div className="absolute top-4 left-12 z-[9999] w-80">
              <HeatmapControls
                enabled={showHeatmap}
                onToggle={setShowHeatmap}
                options={heatmapOptions}
                onOptionsChange={setHeatmapOptions}
                heatmapData={heatmapData}
              />
            </div>
          )}

          {/* Map Legend */}
          {routes.length > 0 && colorBySpeed && showRoutes && (
            <div className="absolute bottom-4 left-4 bg-white rounded-lg shadow p-3 z-[9999]">
              <h4 className="text-sm font-medium text-gray-900 mb-2">
                Map Legend
              </h4>
              <div className="space-y-1 text-xs">
                <div className="flex items-center">
                  <div className="w-4 h-3 bg-green-500 rounded mr-2"></div>
                  <span>Fast (&gt;15 km/h)</span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-3 bg-yellow-500 rounded mr-2"></div>
                  <span>Medium (5-15 km/h)</span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-3 bg-red-500 rounded mr-2"></div>
                  <span>Slow (&lt;5 km/h)</span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-0 border-t-2 border-dashed border-slate-400 mr-2"></div>
                  <span>Gap / transport link</span>
                </div>
              </div>
            </div>
          )}

          {/* Route Statistics Overlay */}
          {routes.length > 0 && !isFullscreen && (
            <div className="absolute bottom-4 right-4 bg-white rounded-lg shadow p-3 z-[9999]">
              <h4 className="text-sm font-medium text-gray-900 mb-2">
                Route Summary
              </h4>
              <div className="space-y-1 text-xs text-gray-600">
                <div>Total Trips: {totalTrips}</div>
                <div>
                  Total Distance:{" "}
                  {routes
                    .reduce((sum, route) => sum + route.total_distance_km, 0)
                    .toFixed(1)}{" "}
                  km
                </div>
                <div>
                  Total Active Time: {formatDuration(totalActiveMinutes)}
                </div>
                <div>
                  Total Points:{" "}
                  {routes
                    .reduce((sum, route) => sum + route.total_points, 0)
                    .toLocaleString()}
                </div>
                {heatmapData && showHeatmap && (
                  <div className="border-t border-gray-200 pt-1 mt-2">
                    <div className="flex items-center text-orange-600">
                      <div className="w-2 h-2 bg-orange-500 rounded-full mr-1"></div>
                      Heatmap: {heatmapData.totalPoints} density points
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
